// pdbq serializes PostGIS geometry as GeoJSON with a legacy `crs` member and
// (for tracks) Z coordinates. Normalize once here for MapLibre.
export type GeoPoint = { type: "Point"; coordinates: number[] };
export type Geometry = {
  type: string;
  coordinates: unknown;
  crs?: unknown;
};

function strip(coords: unknown): unknown {
  if (!Array.isArray(coords)) return coords;
  if (coords.length && typeof coords[0] === "number") return coords.slice(0, 2);
  return coords.map(strip);
}

export function cleanGeometry<T extends Geometry>(geom: T | null | undefined): T | null {
  if (!geom) return null;
  const { crs: _crs, ...rest } = geom;
  return { ...rest, coordinates: strip(geom.coordinates) } as T;
}

// One vertex of an elevation profile: cumulative distance along the track,
// elevation, and the original position (for future map hover sync).
export type ProfilePoint = { d: number; z: number; lng: number; lat: number };

const EARTH_RADIUS_M = 6371000;

// Equirectangular approximation — plenty for per-segment distances of a few
// metres, and cheaper than full haversine.
function segmentDistanceM(a: number[], b: number[]): number {
  const lat1 = (a[1]! * Math.PI) / 180;
  const lat2 = (b[1]! * Math.PI) / 180;
  const x = (((b[0]! - a[0]!) * Math.PI) / 180) * Math.cos((lat1 + lat2) / 2);
  return EARTH_RADIUS_M * Math.hypot(x, lat2 - lat1);
}

/** Flattened coordinate list of a (Multi)LineString, or null for other types. */
export function flatCoords(geom: Geometry | null | undefined): number[][] | null {
  if (!geom) return null;
  if (geom.type === "MultiLineString") return (geom.coordinates as number[][][]).flat();
  if (geom.type === "LineString") return geom.coordinates as number[][];
  return null;
}

// Extract [cumulative distance, elevation] pairs from a (Multi)LineString that
// still carries Z coordinates — call this with the RAW geometry, before
// cleanGeometry strips Z. Segments of a MultiLineString are concatenated and
// the cumulative distance carries across the gap without adding the gap
// itself. Returns null when there is no usable Z data (tracks without
// elevation come through as all-zero Z).
export function elevationProfile(geom: Geometry | null | undefined): ProfilePoint[] | null {
  if (!geom) return null;
  const segments =
    geom.type === "MultiLineString"
      ? (geom.coordinates as number[][][])
      : geom.type === "LineString"
        ? [geom.coordinates as number[][]]
        : null;
  if (!segments) return null;

  const points: ProfilePoint[] = [];
  let d = 0;
  let hasZ = false;
  for (const seg of segments) {
    let prev: number[] | null = null;
    for (const c of seg) {
      const [lng, lat] = c;
      if (typeof lng !== "number" || typeof lat !== "number") continue;
      const z = typeof c[2] === "number" ? c[2] : 0;
      if (z !== 0) hasZ = true;
      if (prev) d += segmentDistanceM(prev, c);
      points.push({ d, z, lng, lat });
      prev = c;
    }
  }
  return hasZ && points.length >= 2 ? points : null;
}

// --- time-aware track stats (track.pointTimes, aligned 1:1 with geometry) ---

// Below ~0.3 km/h a GPS fix is indistinguishable from standing still — jitter
// while paused produces "movement" slower than that, so such samples don't
// count as moving.
const MIN_MOVING_SPEED_KMH = 0.3;
// A gap longer than 5 minutes between fixes means the recording was paused
// (or the device lost signal); counting it would inflate moving time.
const MAX_SAMPLE_GAP_S = 300;

export type TrackTimeStats = {
  /** Wall-clock time from first to last fix, seconds. */
  elapsedS: number;
  /** Time actually in motion, seconds (pauses and standing excluded). */
  movingS: number;
  /** Distance covered while moving, metres. */
  movingDistanceM: number;
  avgSpeedKmh: number;
  paceSecPerKm: number;
};

/**
 * Moving-time stats from a track geometry plus its aligned pointTimes array.
 * Returns null when the times don't align 1:1 with the geometry points, are
 * not monotonically non-decreasing, or there is no measurable movement.
 */
export function trackTimeStats(
  geom: Geometry | null | undefined,
  pointTimes: string[] | null | undefined,
): TrackTimeStats | null {
  const coords = flatCoords(geom);
  if (!coords || !pointTimes || coords.length !== pointTimes.length || coords.length < 2) return null;

  const times = pointTimes.map((t) => Date.parse(t) / 1000);
  if (times.some((t) => !Number.isFinite(t))) return null;
  for (let i = 1; i < times.length; i++) if (times[i]! < times[i - 1]!) return null;

  const elapsedS = times[times.length - 1]! - times[0]!;
  if (elapsedS <= 0) return null;

  let movingS = 0;
  let movingDistanceM = 0;
  for (let i = 1; i < coords.length; i++) {
    const dt = times[i]! - times[i - 1]!;
    if (dt <= 0 || dt > MAX_SAMPLE_GAP_S) continue;
    const dist = segmentDistanceM(coords[i - 1]!, coords[i]!);
    if ((dist / dt) * 3.6 < MIN_MOVING_SPEED_KMH) continue;
    movingS += dt;
    movingDistanceM += dist;
  }
  if (movingS <= 0 || movingDistanceM <= 0) return null;

  const movingKm = movingDistanceM / 1000;
  return {
    elapsedS,
    movingS,
    movingDistanceM,
    avgSpeedKmh: movingKm / (movingS / 3600),
    paceSecPerKm: movingS / movingKm,
  };
}

export function pointCoords(geom: GeoPoint | null | undefined): [number, number] | null {
  if (!geom || geom.type !== "Point") return null;
  const [lng, lat] = geom.coordinates;
  return typeof lng === "number" && typeof lat === "number" ? [lng, lat] : null;
}

export type Feature = {
  type: "Feature";
  geometry: Geometry;
  properties: Record<string, unknown>;
};

export function toFeature(geom: Geometry | null | undefined, properties: Record<string, unknown>): Feature | null {
  const geometry = cleanGeometry(geom);
  return geometry ? { type: "Feature", geometry, properties } : null;
}

export function featureCollection(features: (Feature | null)[]): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: features.filter(Boolean) as never };
}
